import { create } from 'zustand'
import { apiFe } from '@/shared/api/apiFe'
import { settingBot } from '@/features/setting-bot/store/setting'
import { storeFutuExchange } from '@/shared/store/exchange'

interface ManagementDataState {
    // 데이터 (기존 타입 사용)
    filledList: filledList[]
    botList: botList[]
    temporaryList: PostTemporaryList_Res_List[]

    // 무한 스크롤 관련 상태
    filledListPagination: {
        currentPage: number
        totalPages: number
        hasNextPage: boolean
        isFetchingNextPage: boolean
    }

    // 로딩 상태
    isLoading: {
        filledList: boolean
        botList: boolean
        temporaryList: boolean
    }

    // 에러 상태
    error: {
        filledList: string | null
        botList: string | null
        temporaryList: string | null
    }

    // 액션들
    fetchFilledList: (page?: number) => Promise<void>
    fetchNextPage: () => Promise<void>
    fetchBotList: () => Promise<void>
    fetchTemporaryList: () => Promise<void>

    // 삭제/추가/수정 액션
    deleteTemporary: (id_temp: string) => Promise<boolean>
    postTemporary: (data: postDcaReq) => Promise<PostTemporaryList_Res>
    updateTemporary: (item: PostTemporaryList_Res_List, isMobile?: boolean) => void
    startTemporary: (item: PostTemporaryList_Res_List) => Promise<boolean>
    applyTemporaryBySymbol: (symbol: string, positionSide?: string) => boolean

    // 초기화
    resetAll: () => void
}

export const useManagementDataStore = create<ManagementDataState>((set, get) => ({
    // 초기 상태
    filledList: [],
    botList: [],
    temporaryList: [],

    filledListPagination: {
        currentPage: 1,
        totalPages: 1,
        hasNextPage: false,
        isFetchingNextPage: false
    },

    isLoading: {
        filledList: false,
        botList: false,
        temporaryList: false
    },

    error: {
        filledList: null,
        botList: null,
        temporaryList: null
    },

    // filledList 가져오기
    fetchFilledList: async (page = 1) => {
        set(state => ({
            isLoading: { ...state.isLoading, filledList: true },
            error: { ...state.error, filledList: null }
        }))

        try {
            const response = await apiFe.post<postFilledListRes>('/bot/filled/list', {
                pagesize: "1000", // 한 번에 1000개씩
                req_pagenum: page.toString()
            }, { headers: { 'x-skip-loading': 'true' } })

            const filledList = response.data.list || []
            const totalPages = Number(response.data.totalpages) || 1
            const hasNextPage = page < totalPages

            set(state => ({
                filledList,
                filledListPagination: {
                    ...state.filledListPagination,
                    currentPage: page,
                    totalPages,
                    hasNextPage,
                    isFetchingNextPage: false
                },
                isLoading: { ...state.isLoading, filledList: false }
            }))
        } catch (error) {
            console.error('filledList 가져오기 실패:', error)
            set(state => ({
                error: { ...state.error, filledList: '데이터를 가져오는데 실패했습니다.' },
                isLoading: { ...state.isLoading, filledList: false }
            }))
        }
    },

    // 다음 페이지 가져오기 (무한 스크롤)
    fetchNextPage: async () => {
        const state = get()
        const { currentPage, hasNextPage, isFetchingNextPage } = state.filledListPagination

        if (!hasNextPage || isFetchingNextPage) {
            console.log("다음 페이지 없음 또는 이미 로딩 중")
            return
        }

        set(state => ({
            filledListPagination: {
                ...state.filledListPagination,
                isFetchingNextPage: true
            }
        }))

        try {
            const nextPage = currentPage + 1
            const response = await apiFe.post<postFilledListRes>('/bot/filled/list', {
                pagesize: "100",
                req_pagenum: nextPage.toString()
            })

            const newFilledList = response.data.list || []
            const totalPages = Number(response.data.totalpages) || 1
            const hasNextPage = nextPage < totalPages

            // 기존 리스트에 새 데이터 추가
            set(state => ({
                filledList: [...state.filledList, ...newFilledList],
                filledListPagination: {
                    ...state.filledListPagination,
                    currentPage: nextPage,
                    totalPages,
                    hasNextPage,
                    isFetchingNextPage: false
                }
            }))

            console.log("다음 페이지 가져오기 성공:", newFilledList.length, "총 개수:", state.filledList.length + newFilledList.length)
        } catch (error) {
            console.error('다음 페이지 가져오기 실패:', error)
            set(state => ({
                filledListPagination: {
                    ...state.filledListPagination,
                    isFetchingNextPage: false
                }
            }))
        }
    },

    // botList 가져오기
    fetchBotList: async () => {
        set(state => ({
            isLoading: { ...state.isLoading, botList: true },
            error: { ...state.error, botList: null }
        }))

        try {
            const response = await apiFe.get<postBotListRes>('/bot/list')
            const botList = response.data.list || []

            set(state => ({
                botList,
                isLoading: { ...state.isLoading, botList: false }
            }))

            // console.log("botList 가져오기 성공:", botList)
        } catch (error) {
            console.error('botList 가져오기 실패:', error)
            set(state => ({
                error: { ...state.error, botList: '데이터를 가져오는데 실패했습니다.' },
                isLoading: { ...state.isLoading, botList: false }
            }))
        }
    },

    // temporaryList 가져오기
    fetchTemporaryList: async () => {
        set(state => ({
            isLoading: { ...state.isLoading, temporaryList: true },
            error: { ...state.error, temporaryList: null }
        }))

        try {
            const response = await apiFe.get<PostTemporaryList_Res>('/temporary')
            const temporaryList = response.data.list || []

            set(state => ({
                temporaryList,
                isLoading: { ...state.isLoading, temporaryList: false }
            }))

            // console.log("temporaryList 가져오기 성공:", temporaryList.length)
        } catch (error) {
            console.error('temporaryList 가져오기 실패:', error)
            set(state => ({
                error: { ...state.error, temporaryList: '데이터를 가져오는데 실패했습니다.' },
                isLoading: { ...state.isLoading, temporaryList: false }
            }))
        }
    },

    // temporary 삭제
    deleteTemporary: async (id_temp: string) => {
        try {
            console.log("삭제 시작:", id_temp)
            const result = await apiFe.delete('/temporary', {
                data: { id_temp }
            })

            if (result.data.result_code === '100') {
                console.log("삭제 성공")
                // 삭제 후 리스트 다시 가져오기
                await get().fetchTemporaryList()
                return true
            } else {
                console.log("삭제 실패:", result.data)
                return false
            }
        } catch (error) {
            console.error('삭제 중 오류:', error)
            return false
        }
    },

    // temporary 추가
    postTemporary: async (data: postDcaReq) => {

        console.log("추가 시작:", data)
        const result = await apiFe.post<PostTemporaryList_Res>('/temporary', data)

        if (result.data.result_code === '100') {
            const { alertStore } = await import('@/shared/store/alert')
            alertStore.getState().setAlert({
                message: `임시저장이 완료되었습니다.`
            })
            // 추가 후 리스트 다시 가져오기
            await get().fetchTemporaryList()

        }
        return result.data

    },

    // temporary 수정
    updateTemporary: (item: PostTemporaryList_Res_List, isMobile?: boolean) => {
        const { updateSetting, setTab } = settingBot.getState()
        storeFutuExchange.getState().setExSymbol(item.id_tactic + 'USDT')

        // required 섹션 업데이트
        updateSetting('required.id_tactic', item.id_tactic)
        updateSetting('required.id_ex', item.id_ex || '')
        updateSetting('required.position_side', item.position_side)

        // 저장된 레버리지가 현재 최대 레버리지보다 큰 경우에만 보정
        try {
            const symbol = (item.id_tactic || '').toUpperCase() + 'USDT'
            const leverageBrackets = storeFutuExchange.getState().leverageBrackets
            const brackets = leverageBrackets?.get(symbol)?.brackets || []
            if (brackets.length > 0) {
                const maxLeverage = Number(brackets[0]?.initialLeverage) || 1
                const savedLeverage = Number(item.leverage)
                const finalLeverage = savedLeverage > maxLeverage ? String(maxLeverage) : item.leverage
                updateSetting('required.leverage', finalLeverage)
            } else {
                updateSetting('required.leverage', item.leverage)
            }
        } catch (e) {
            // 문제가 있으면 원본 값 사용
            updateSetting('required.leverage', item.leverage)
        }

        updateSetting('required.margin_mode', item.margin_mode)
        updateSetting('required.tp_entry_initial', item.tp_entry_initial)
        updateSetting('required.price_initial_limit', item.price_initial_limit || '')
        updateSetting('required.pct_take_profit', item.pct_take_profit)
        updateSetting('required.funds_per_asset', item.funds_per_asset)

        // hedging 섹션 업데이트
        updateSetting('hedging.hedge_ratio', item.hedge_ratio || '')
        updateSetting('hedging.hedge_usdt', item.hedge_usdt || '')

        // tp 섹션 업데이트
        updateSetting('tp.tp_setting', item.tp_setting)
        updateSetting('tp.yn_use_pct_tp', !item.pct_take_profit || item.pct_take_profit === '0' ? 'Y' : 'N')
        updateSetting('tp.simple_setting.cnt_entry', item.simple_setting.cnt_entry || '')
        updateSetting('tp.simple_setting.rate_extra_entry', item.simple_setting.rate_extra_entry || '')
        updateSetting('tp.simple_setting.gab_extra_entry', item.simple_setting.gab_extra_entry || '')

        // detail_setting 배열 업데이트
        if (item.tp_setting === 'detail' && item.detail_setting) {
            const { setDetailSetting } = settingBot.getState()
            setDetailSetting(item.detail_setting)
        }

        // option 섹션 업데이트
        updateSetting('option.pct_stop_loss', item.pct_stop_loss || '')
        updateSetting('option.yn_repeat', item.yn_repeat)
        updateSetting('option.yn_stop_loss', item.yn_stop_loss || '')
        updateSetting('option.tp_entry_additional', item.tp_entry_additional)
        updateSetting('option.tp_repeat', item.tp_repeat)
        updateSetting('option.threshold_price', item.threshold_price || '')
        updateSetting('option.yn_use_threshold_price', item.threshold_price ? 'Y' : 'N')

        // 탭 설정 (tp_setting이 'simple' 또는 'detail'인지 확인)
        if (item.tp_setting === 'simple' || item.tp_setting === 'detail') {
            setTab(item.tp_setting)
        }
    },

    // temporary 시작
    startTemporary: async (item: PostTemporaryList_Res_List) => {
        try {
            console.log("봇 시작:", item.id_temp)

            // 봇이 이미 실행 중인지 확인
            const { botList } = get()
            const isBotRunning = !!botList?.some((bot) =>
                bot.coin === item.id_tactic && bot.position_side === item.position_side
            )

            if (isBotRunning) {
                // alertStore 직접 사용
                const { alertStore } = await import('@/shared/store/alert')
                alertStore.getState().setAlert({
                    message: `${item.id_tactic}USDT ${item.position_side} 봇이 이미 실행 중입니다.`
                })
                return false
            }

            // 포지션이 존재하는지 확인
            const { userHavePosition } = (await import('@/shared/store/userInfo')).storeFutuUserInfo.getState()
            console.log("userHavePosition :::", userHavePosition, item)
            const hasPosition = userHavePosition.has(`${item.id_tactic.toUpperCase() + 'USDT'}_${item.position_side.toUpperCase()}`)

            if (hasPosition) {
                // alertStore 직접 사용
                const { alertStore } = await import('@/shared/store/alert')
                alertStore.getState().setAlert({
                    message: `${item.id_tactic}USDT ${item.position_side} 포지션이 이미 존재합니다.`
                })
                return false
            }

            // 먼저 설정을 업데이트
            const { updateSetting, setTab, setCheckModalOpen } = settingBot.getState()

            // required 섹션 업데이트
            updateSetting('required.id_tactic', item.id_tactic)
            updateSetting('required.id_ex', item.id_ex || '')
            updateSetting('required.position_side', item.position_side)
            // 저장된 레버리지가 현재 최대 레버리지보다 큰 경우에만 보정
            try {
                const symbol = (item.id_tactic || '').toUpperCase() + 'USDT'
                const leverageBrackets = storeFutuExchange.getState().leverageBrackets
                const brackets = leverageBrackets?.get(symbol)?.brackets || []
                if (brackets.length > 0) {
                    const maxLeverage = Number(brackets[0]?.initialLeverage) || 1
                    const savedLeverage = Number(item.leverage)
                    const finalLeverage = savedLeverage > maxLeverage ? String(maxLeverage) : item.leverage
                    updateSetting('required.leverage', finalLeverage)
                } else {
                    updateSetting('required.leverage', item.leverage)
                }
            } catch (e) {
                updateSetting('required.leverage', item.leverage)
            }
            updateSetting('required.margin_mode', item.margin_mode)
            updateSetting('required.tp_entry_initial', item.tp_entry_initial)
            updateSetting('required.price_initial_limit', item.price_initial_limit || '')
            updateSetting('required.pct_take_profit', item.pct_take_profit)
            updateSetting('required.funds_per_asset', item.funds_per_asset)

            // hedging 섹션 업데이트
            updateSetting('hedging.hedge_ratio', item.hedge_ratio || '')
            updateSetting('hedging.hedge_usdt', item.hedge_usdt || '')

            // tp 섹션 업데이트
            updateSetting('tp.tp_setting', item.tp_setting)
            updateSetting('tp.yn_use_pct_tp', !item.pct_take_profit || item.pct_take_profit === '0' ? 'Y' : 'N')
            updateSetting('tp.simple_setting.cnt_entry', item.simple_setting.cnt_entry || '')
            updateSetting('tp.simple_setting.rate_extra_entry', item.simple_setting.rate_extra_entry || '')
            updateSetting('tp.simple_setting.gab_extra_entry', item.simple_setting.gab_extra_entry || '')

            // detail_setting 배열 업데이트
            if (item.tp_setting === 'detail' && item.detail_setting) {
                const { setDetailSetting } = settingBot.getState()
                setDetailSetting(item.detail_setting)
            }

            // option 섹션 업데이트
            updateSetting('option.pct_stop_loss', item.pct_stop_loss || '')
            updateSetting('option.yn_repeat', item.yn_repeat)
            updateSetting('option.yn_stop_loss', item.yn_stop_loss || '')
            updateSetting('option.tp_entry_additional', item.tp_entry_additional)
            updateSetting('option.tp_repeat', item.tp_repeat)
            updateSetting('option.threshold_price', item.threshold_price || '')
            updateSetting('option.yn_use_threshold_price', item.threshold_price ? 'Y' : 'N')

            // 탭 설정 (tp_setting이 'simple' 또는 'detail'인지 확인)
            if (item.tp_setting === 'simple' || item.tp_setting === 'detail') {
                setTab(item.tp_setting)
            }

            // 체크 모달 열기
            setCheckModalOpen(true)

            // 체크 모달에서 확인 후 봇 시작 로직은 useCheckModal에서 처리
            return true
        } catch (error) {
            console.error('봇 시작 중 오류:', error)
            return false
        }
    },

    applyTemporaryBySymbol: (symbol: string, positionSide?: string) => {
        const { temporaryList } = get()

        if (!temporaryList || temporaryList.length === 0) {
            // 임시저장 데이터가 전혀 없는 경우: store의 기본값 리셋 API 활용
            const { resetDefaultsForSymbol, setTab } = settingBot.getState()
            const symbolWithoutUSDT = symbol.replace('USDT', '')
            resetDefaultsForSymbol(symbolWithoutUSDT, (positionSide as 'LONG' | 'SHORT') || 'LONG')
            setTab('simple')
            return true
        }

        // 심볼에서 USDT 제거 (예: BTCUSDT -> BTC)
        const symbolWithoutUSDT = symbol.replace('USDT', '')

        // 해당 심볼의 임시저장 데이터 찾기
        let temporaryData = temporaryList.find(item => item.id_tactic === symbolWithoutUSDT)

        // 포지션 사이드가 지정된 경우 해당 포지션의 데이터만 찾기
        if (positionSide && temporaryData) {
            temporaryData = temporaryList.find(item =>
                item.id_tactic === symbolWithoutUSDT &&
                item.position_side === positionSide
            )
        }

        if (temporaryData) {
            get().updateTemporary(temporaryData)
            return true
        }

        // 임시저장 데이터가 없으면 기본값으로 세팅 (해당 심볼로 첫 설정)
        const { resetDefaultsForSymbol, setTab } = settingBot.getState()
        storeFutuExchange.getState().setExSymbol(symbol)
        resetDefaultsForSymbol(symbolWithoutUSDT, (positionSide as 'LONG' | 'SHORT') || 'LONG')
        setTab('simple')
        return true
    },

    // 모든 데이터 초기화
    resetAll: () => {
        set({
            filledList: [],
            botList: [],
            temporaryList: [],
            filledListPagination: {
                currentPage: 1,
                totalPages: 1,
                hasNextPage: false,
                isFetchingNextPage: false
            },
            isLoading: {
                filledList: false,
                botList: false,
                temporaryList: false
            },
            error: {
                filledList: null,
                botList: null,
                temporaryList: null
            }
        })
    }
})) 